import Image from "~/components/ui/Image";
import { type SectionProps } from "~/types/deco";
import { useId } from "react";
import { matchPathname } from "~/sdk/matchPathname";
import { withDynamicOutletHighlights } from "~/sdk/carouselSizeHref";
import Slider from "~/components/ui/Slider";
import SliderJS from "~/components/ui/SliderJS";

interface HighlightCard {
  text: string;
  href?: string;
}

export interface CarouselSizeContent {
  title?: string;
  highlights?: HighlightCard[];
  colorDefault?: boolean;
}

export interface Props {
  title?: string;
  highlights: HighlightCard[];
  /**
   * @title Ativar cor de Black Friday?
   * @description Ativa as cores de Black Friday e remove a de Natal
   */
  colorDefault?: boolean;
  /** @description URL pattern to display this section. Use * as wildcard. Example: /outlet* */
  matcher?: string;
}

export const loader = (props: Props, req: Request) => {
  const { matcher, title, highlights, colorDefault } = props;
  const { pathname } = new URL(req.url);

  if (matcher && !matchPathname(matcher, pathname)) {
    return { title, highlights: undefined, colorDefault };
  }

  return {
    title,
    highlights: withDynamicOutletHighlights(highlights, pathname),
    colorDefault,
  };
};

function CarouselSize(
  { title, highlights, colorDefault }: CarouselSizeContent,
) {
  const id = useId();

  if (!highlights?.length) {
    return null;
  }

  return (
    <>
      <section className="flex flex-col items-center mt-10">
        {title && (
          <h2 className="flex justify-center items-center font-['Poppins'] not-italic font-normal text-[19px] md:text-[32px] leading-[35px] uppercase text-black mb-4 md:mb-8">
            {title}
          </h2>
        )}
        <div
          id={id}
          className="h-20 w-full grid grid-cols-[70px_1fr_70px] xl:max-w-[704px]"
        >
          <div className="flex justify-center col-span-full row-start-2 row-end-5 h-full w-full overflow-hidden">
            <Slider className="carousel carousel-center sm:carousel-end gap-[21px] h-full min-w-0 flex-shrink md:gap-6 pl-4 pr-4 xl:pl-0 xl:pr-0 [mask-image:linear-gradient(to_right,transparent_0,black_16px,black_calc(100%-16px),transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0,black_16px,black_calc(100%-16px),transparent_100%)]">
              {highlights.map((highlight, index) => {
                return (
                  <Slider.Item
                    index={index}
                    className="carousel-item w-[40%] lg:w-[12.5%] flex justify-center shrink-0 max-w-15 min-h-15 max-h-15 xl:max-w-20 xl:min-h-20 xl:max-h-20"
                  >
                    <a
                      href={highlight.href}
                      className={`text-center bg-[#C18A58] hover:bg-[#D1B382] text-[#000000] hover:text-[#F1F2E8] flex w-full rounded-full justify-center items-center font-normal text-2xl xl:text-[34px]`}
                    >
                      {highlight.text}
                    </a>
                  </Slider.Item>
                );
              })}
            </Slider>
          </div>
          <>
            <div className="relative hidden lg:block z-10 col-start-1 row-start-3 left-[-70px] top-[-15px]">
              <Slider.PrevButton className="absolute right-1/2 bg-white h-[30px] w-[30px] flex justify-center items-center rounded-full border border-solid border-[#f9f9f9]">
                <Image
                  width={12}
                  height={23}
                  src={"https://decoims.com/danielatombini/8fd8f8e4-9e5e-4d61-b837-d36704dd7994/home_slick-arrow.png"}
                  alt="Previous item"
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                />
              </Slider.PrevButton>
            </div>
            <div className="relative hidden lg:block z-10 col-start-3 row-start-3 right-[-70px] top-[-15px]">
              <Slider.NextButton className="absolute left-1/2 bg-white h-[30px] w-[30px] flex justify-center items-center rounded-full border border-solid border-[#f9f9f9] rotate-180">
                <Image
                  width={12}
                  height={23}
                  src={"https://decoims.com/danielatombini/8fd8f8e4-9e5e-4d61-b837-d36704dd7994/home_slick-arrow.png"}
                  alt="Next item"
                  loading="lazy"
                  fetchPriority="low"
                  decoding="async"
                />
              </Slider.NextButton>
            </div>
          </>
          <SliderJS rootId={id} infinite />
        </div>
      </section>
    </>
  );
}

export default function CarouselSizeSection(
  props: SectionProps<typeof loader>,
) {
  return <CarouselSize {...props} />;
}

export { CarouselSize };
